import React, { useCallback, useEffect, useMemo, useState } from "react";
import {Offcanvas, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useFilters } from "../../context/GlobalFilterContext";
import { normalizeAccount } from "../../utils/normalizeAccount";
import { createAuthFetch } from "../../utils/authFetch";

import SummarySidebar from "./components/SummarySidebar";
import SummaryHistoryChart from "./components/SummaryHistoryChart";
import SummaryTableCard from "./components/SummaryTableCard";
import { SUMMARY_METRICS } from "./config/summaryMetrics.config";
import { useSummaryTable } from "./hooks/useSummaryTable";
import { useSummaryDetail } from "./hooks/useSummaryDetail";

import AnalysisChartProducts from "../../components/analytics/AnalysisChartProducts";

const PAGE_SIZE = 100;

const SummaryDashboardPage = () => {
  const navigate = useNavigate();
  const authFetch = useMemo(() => createAuthFetch(navigate), [navigate]);
  const { account, marketplace } = useFilters();

  const [activeMetricId, setActiveMetricId] = useState("activeListings");
  const [sidebarItems, setSidebarItems] = useState([]);
  const [loadingSidebar, setLoadingSidebar] = useState(false);
  const [sidebarError, setSidebarError] = useState(null);

  // full metric detail cache: history + first table payload
  const [detailCache, setDetailCache] = useState({});
  // table-only cache: metric + page + search
  const [tableCache, setTableCache] = useState({});

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [showAnalyticsPreview, setShowAnalyticsPreview] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const isAllAccountsSelected =
    account === "0" || account === 0 || account === null || account === "null";

  const selectedAccount = isAllAccountsSelected ? null : normalizeAccount(account);
  const selectedMarketplace = marketplace;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    loadingDetail,
    detailError,
    activeDetail,
    historyData,
    metricDetailKey,
  } = useSummaryDetail({
    activeMetricId,
    selectedAccount,
    selectedMarketplace,
    authFetch,
    detailCache,
    setDetailCache,
    setTableCache,
  });

  useEffect(() => {
    setCurrentPage(1);
    setTableSearch("");
    setAppliedSearch("");
  }, [activeMetricId]);

  useEffect(() => {
    const loadSidebar = async () => {
      if (isAllAccountsSelected || !selectedAccount || !selectedMarketplace) {
        setSidebarItems([]);
        setLoadingSidebar(false);
        setSidebarError(null);
        return;
      }

      try {
        setLoadingSidebar(true);
        setSidebarError(null);

        const response = await authFetch(
          `/amz-api/dashboard/summary?account=${selectedAccount}&marketplace=${selectedMarketplace}`
        );

        if (!response) return;

        const data = await response.json();

        const mappedItems = SUMMARY_METRICS.map((metric) => ({
          id: metric.id,
          label: metric.label,
          description: metric.description,
          value: data[metric.id] ?? 0,
          tone: metric.tone,
          bgClass: metric.bgClass,
          textClass: metric.textClass,
          defaultViewType: metric.defaultViewType,
        }));

        setSidebarItems(mappedItems);
      } catch (error) {
        setSidebarError("Failed to load dashboard metrics");
      } finally {
        setLoadingSidebar(false);
      }
    };

    loadSidebar();
  }, [isAllAccountsSelected, selectedAccount, selectedMarketplace, authFetch]);

  const {
    tableSearch,
    setTableSearch,
    appliedSearch,
    setAppliedSearch,
    currentPage,
    setCurrentPage,
    loadingTable,
    tableCacheKey
  } = useSummaryTable({
    activeMetricId,
    selectedAccount,
    selectedMarketplace,
    authFetch,
    tableCache,
    setTableCache
  });

  const activeMetric = useMemo(
    () => SUMMARY_METRICS.find((metric) => metric.id === activeMetricId),
    [activeMetricId]
  );
  const chartColor = activeMetric?.chartColor || "#6c757d";

  const firstTableKey = `${selectedAccount}__${selectedMarketplace}__${activeMetricId}__1__`;
  const activeTable =
    tableCache[tableCacheKey] ||
    (currentPage === 1 && appliedSearch === "" ? tableCache[firstTableKey] : null) ||
    null;

  const fallbackTableKey = `${selectedAccount}__${selectedMarketplace}__${activeMetricId}__1__${appliedSearch}`;
  const fallbackTable = tableCache[fallbackTableKey] || tableCache[firstTableKey] || null;

  // Поки нова сторінка вантажиться, показуємо попередню доступну таблицю
  const displayedTable = activeTable || fallbackTable || null;

  const activeColumns = useMemo(
    () => displayedTable?.columns || [],
    [displayedTable]
  );
  const activeRows = useMemo(
    () => displayedTable?.rows || [],
    [displayedTable]
  );

  const totalRecords = activeTable?.records ?? fallbackTable?.records ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  useEffect(() => {
    if (loadingTable) return;
    if (!activeTable) return;

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, loadingTable, activeTable]);

  const pageNumbers = useMemo(() => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from(
      { length: end - start + 1 },
      (_, index) => start + index
    );
  }, [currentPage, totalPages]);

  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, totalRecords);

  const handleOpenAnalytics = useCallback((product) => {
    setSelectedProduct(product);
    setShowAnalyticsPreview(true);
  }, []);

  const handleCloseAnalytics = useCallback(() => {
    setShowAnalyticsPreview(false);
    setSelectedProduct(null);
  }, []);

  return (
    <div className="container-fluid main-container summary-dashboard h-100 flex-grow-1">

    {isAllAccountsSelected ? (
      <div className="alert alert-warning mb-0">
        Select an account to view the Summary Dashboard.
      </div>
    ) : (
      <div className="d-flex flex-column flex-lg-row gap-3">
        <div className="summary-sidebar-container">
          {loadingSidebar && (
            <div className="alert alert-info">
              <Spinner animation="grow" variant="secondary" size="sm" />{" "}
              Data analysis...
            </div>
          )}

          {sidebarError && (
            <div className="alert alert-danger mb-0">{sidebarError}</div>
          )}

          {!loadingSidebar && !sidebarError && (
            <SummarySidebar
              items={sidebarItems}
              activeMetricId={activeMetricId}
              onChange={setActiveMetricId}
            />
          )}
        </div>

        <div className="summary-detail-container flex-grow-1">
          <div className="card h-100">
            <div className="card-body">
              <h4 className="card-title mb-3">
                {activeMetric?.label || "Summary detail"}
              </h4>

              <div className="border rounded p-3 bg-light mb-3">
                {activeMetric?.description || activeMetricId}
              </div>

              {loadingDetail && !activeDetail && (
                <div className="d-flex align-items-center gap-2 mb-2 text-muted">
                  <Spinner animation="grow" variant="secondary" size="sm" />{" "}
                  Loading data...
                </div>
              )}

              {detailError && (
                <div className="alert alert-danger">{detailError}</div>
              )}

              {!detailError &&
                ((Array.isArray(historyData) && historyData.length > 0) ||
                  (!Array.isArray(historyData) &&
                    historyData &&
                    Array.isArray(historyData.categories) &&
                    Array.isArray(historyData.series) &&
                    historyData.categories.length > 0 &&
                    historyData.series.length > 0)) && (
                    <SummaryHistoryChart
                      historyData={historyData}
                      chartColor={chartColor}
                      isMobile={isMobile}
                    />
              )}

              {!detailError && displayedTable && (
                <SummaryTableCard
                  totalRecords={totalRecords}
                  tableSearch={tableSearch}
                  setTableSearch={setTableSearch}
                  setAppliedSearch={setAppliedSearch}
                  setCurrentPage={setCurrentPage}
                  loadingTable={loadingTable}
                  activeColumns={activeColumns}
                  activeRows={activeRows}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageNumbers={pageNumbers}
                  isMobile={isMobile}
                  tableType={activeMetric.tableType}
                  onOpenAnalytics={handleOpenAnalytics}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    <Offcanvas
      show={showAnalyticsPreview}
      onHide={handleCloseAnalytics}
      placement={isMobile ? "bottom" : "end"}
      className={`product-analytics-preview ${isMobile ? "mobile" : "desktop"}`}
      style={isMobile ? {} : { width: "75vw", maxWidth: "1400px" }}
    >
      <Offcanvas.Header closeButton className="product-analytics-preview__header">
        <div className="d-flex align-items-start justify-content-between gap-3 w-100">
          <Offcanvas.Title
            className="product-analytics-preview__title"
            title={selectedProduct?.title || selectedProduct?.name || selectedProduct?.asin || "Product analytics"}
          >
            {selectedProduct?.title || selectedProduct?.name || selectedProduct?.asin || "Product analytics"}
          </Offcanvas.Title>

          {selectedProduct?.asin && (
            <a
              href={`/?asin=${selectedProduct.asin}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-outline-primary flex-shrink-0 me-4"
            >
              Open full dashboard
            </a>
          )}
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="product-analytics-preview__body">
        {selectedProduct ? (
          <div className="product-analytics-preview__content">
            <AnalysisChartProducts
              account={account}
              marketplace={selectedMarketplace}
              asins={[selectedProduct.asin]}
            />
          </div>
        ) : null}
      </Offcanvas.Body>
    </Offcanvas>

    </div>
  );
};

export default SummaryDashboardPage;
